import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ConfigItemStatusControl } from './ConfigItemStatusControl';
import { categoryLabel } from '../utils/discoveryGrid';
import { isLocked, labelFor, normalizeStatus, type LifecycleStatus } from '../utils/configItemLifecycle';
import { listConfigItems, setImplementationStatus } from '../utils/discoveryGridApi';

/**
 * BuildBacklog (ledger L28; step 4 of the simplification plan): the config items locked for
 * build, as one list with the status control. One lifecycle status per item
 * (docs/CONFIG_ITEM_LIFECYCLE.md): the client locks, approves and sends back; the firm builds,
 * hands to review, resets and blocks. An unlocked item leaves the list.
 */

interface BuildBacklogProps {
  companyId: string;
  accessToken: string;
  currentUser: any;
  isConsultant: boolean;
}

const ORDER: LifecycleStatus[] = ['ready_for_build', 'ready_for_review', 'in_build', 'approved', 'blocked', 'discovery'];

/** In the backlog: locked for build (ready_for_build and after). */
export function inBacklog(item: any): boolean {
  return !!item && isLocked(normalizeStatus(item));
}

/** The status the control shows: the item's one lifecycle status, legacy spellings folded. */
export function backlogStatus(item: any): LifecycleStatus {
  return normalizeStatus(item);
}

export function BuildBacklog({ companyId, accessToken, isConsultant }: BuildBacklogProps) {
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await listConfigItems(companyId, accessToken);
      const backlog = all.filter(inBacklog).sort((a, b) => ORDER.indexOf(backlogStatus(a)) - ORDER.indexOf(backlogStatus(b)) || String(a.name).localeCompare(String(b.name)));
      setItems(backlog);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the backlog');
      setItems([]);
    }
  }, [companyId, accessToken]);

  useEffect(() => { load(); }, [load]);

  const move = async (item: any, status: LifecycleStatus, comment?: string) => {
    try {
      await setImplementationStatus(companyId, item.id, { status, comment }, accessToken);
      toast.success(`${item.name}: ${labelFor(status).toLowerCase()}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Could not change the status');
    }
  };

  return (
    <Card data-testid="build-backlog">
      <CardHeader>
        <CardTitle>Build backlog</CardTitle>
        <CardDescription>Items locked for build. The client locks, approves and sends back; the consultant builds, hands to review and blocks.</CardDescription>
      </CardHeader>
      <CardContent>
        {items === null ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading the backlog…</div>
        ) : error ? (
          <div className="text-sm text-gray-600">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-600">Nothing is ready for build yet. Items arrive here when their discovery is locked.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid="backlog-row">
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-gray-600">{categoryLabel(item.category)}</TableCell>
                  <TableCell>
                    <ConfigItemStatusControl item={item} firm={isConsultant} onMove={(status, comment) => move(item, status, comment)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
